import { TextInput } from '@components/shared/form/TextInput';
import CustomSelect from '@components/shared/form/CustomSelectInput';
import { useTexts } from 'utils/useTexts';
import { Button } from '@components/shared/Button/Button';
import { useFormContext } from '@context/FormContext';
import { countryStates } from '@shared/constants/countryStates';
import useForm, { validate } from '@shared/hooks/useForm';
import { createErrorResponse, getContactData } from '@services/utils';
import { CDX_ACTIONS } from 'types/form';
import { updateLenderInformation } from '@services/lenderInformation';
import useEffectOnce from '@shared/hooks/useEffectOnce';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import { PhoneTextInput } from '@components/shared/form/PhoneTextInput';
import { PriceInput } from '@components/shared/form/PriceTextInput';
import { SEGMENT_SCREEN_PARAMS } from '@shared/constants/segmentConstants';
import { camelToSnake, convertArrayToSnakeCase, createMask } from 'utils/stringFormat';
import { useScreenNavigator } from '@shared/hooks/useScreenNavigator';

interface ILenderDetailsForm {
  lenderName: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  phoneNumber: string;
  accountNumber: string;
  currentBalance: number;
}
const LenderInformationForm = () => {
  const { t } = useTexts();
  const { next } = useScreenNavigator();
  const { trackFormSubmit, trackPage, startInteraction } = useAnalytics(SEGMENT_SCREEN_PARAMS.LENDER_DETAILS);

  const { currentStep, submitStart, submitSuccess, dispatch, isLoading } = useFormContext();

  const contactData = getContactData();
  const { errors, handleChange, handleSubmit, values } = useForm<ILenderDetailsForm>({
    initialValues: {
      lenderName: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      phoneNumber: '',
      accountNumber: '',
      currentBalance: 0,
    },
    validationSchema: {
      lenderName: validate.string('lenderName').required(t('lenderInformation.validation.currentLenderName')),
      address: validate.string('address').required(t('lenderInformation.validation.lenderAddress')),
      city: validate.string('city').required(t('lenderInformation.validation.lenderCity')),
      state: validate.string('state').required(t('lenderInformation.validation.lenderState')),
      zipCode: validate
        .string('zipCode')
        .required(t('lenderInformation.validation.lenderZipCode'))
        .matches(/^\d{5}$/, t('lenderInformation.validation.lenderValidZipcode')),
      phoneNumber: validate
        .string('phoneNumber')
        .required(t('lenderInformation.validation.lenderPhoneNumber'))
        .matches(/^\d{10}$/, t('lenderInformation.validation.lenderValidPhoneNumber')),
      accountNumber: validate.string('accountNumber').required(t('lenderInformation.validation.loanAccountNumber')),
      currentBalance: validate
        .number('currentBalance')
        .minNumber(1, t('lenderInformation.validation.remainingLoanBalance.required'))
        .isNumber(t('lenderInformation.validation.remainingLoanBalance.isNumber')),
    },
    onSubmit: async (values) => {
      submitStart();
      const selectedLoanDetails = {
        leadId: contactData?.leadId,
        payment: contactData?.currentMonthlyPayment,
        ...values,
      };
      const submitResponse = await updateLenderInformation(
        {
          contactId: contactData?.contactId,
          assetId: contactData?.assetId,
        },
        selectedLoanDetails
      );

      if (!submitResponse) {
        trackFormSubmit('No asset id found for lender information update navigated to first screen');
        localStorage.clear();
        dispatch({
          type: CDX_ACTIONS.SUBMIT_API_ERROR,
          payload: createErrorResponse(400, 'Error while submitting lender information, please try again'),
        });
        return;
      }
      if ('error' in submitResponse) {
        trackFormSubmit('Form Submitted', {
          submission_status: false,
          submission_method: 'manual',
          submission_fail_reason: camelToSnake(submitResponse?.error?.message ?? ''),
        });
        trackFormSubmit('Lender Information Provided', {
          submission_method: 'manual',
          form_context: 'lender_details',
          current_lender_name: values.lenderName,
          qualified_fail_reason: 'other',
          qualified_status: false,
          completed_status: false,
        });
        dispatch({
          type: CDX_ACTIONS.SUBMIT_API_ERROR,
          payload: submitResponse,
        });
      } else {
        submitSuccess();

        trackFormSubmit('Form Submitted', {
          submission_status: true,
          submission_method: 'manual',
          current_lender_name: values.lenderName ?? '',
          current_lender_phone: values.phoneNumber ?? '',
          loan_account_number_masked: createMask('*', 4, values?.accountNumber ?? ''),
          forward_screen_name: 'offers_confirmation',
        });

        trackFormSubmit('Lender Information Provided', {
          submission_method: 'manual',
          form_context: 'lender_details',
          current_lender_name: values.lenderName ?? '',
          qualified_status: true,
          completed_status: true,
        });
      }
      next(currentStep, 'postSubmitOutcome', { submitResponse}, true);
    },
    onValidationError: (errors) => {
      trackFormSubmit('Form Submitted', {
        submission_status: false,
        submission_method: 'manual',
        submission_fail_reason: 'validation_error',
        validation_errors: convertArrayToSnakeCase(Object.keys(errors)),
      });
    },
  });

  const handleInputChange = <K extends keyof ILenderDetailsForm>(field: K, value: ILenderDetailsForm[K]) => {
    handleChange(field, value);
  };

  useEffectOnce(() => trackPage(t('pageTitles.lenderInformation')));

  return (
    <div>
      <form onSubmit={handleSubmit} onFocus={startInteraction}>
        <h1 className="iLendingCDX-form-title">{t('lenderInformation.title')}</h1>

        <h2 className="iLendingCDX-form-title--small iLendingCDX-mb-4 ">{t('lenderInformation.enterCurrentLoanInfo')}</h2>
        <div>
          <TextInput
            label={t('lenderInformation.form.currentLenderName')}
            className={'iLendingCDX-mb-6'}
            name="lenderName"
            errorMessage={errors.lenderName}
            onChange={(e) => handleInputChange('lenderName', e.target.value)}
          />

          <TextInput
            label={t('lenderInformation.form.lenderAddress')}
            className={'iLendingCDX-mb-6'}
            name="address"
            errorMessage={errors.address}
            onChange={(e) => handleInputChange('address', e.target.value)}
          />
          <TextInput
            label={t('lenderInformation.form.lenderCity')}
            className={'iLendingCDX-mb-6'}
            name="city"
            errorMessage={errors.city}
            onChange={(e) => handleInputChange('city', e.target.value)}
          />

          <CustomSelect
            label={t('lenderInformation.form.lenderState')}
            className={'iLendingCDX-mb-6'}
            options={countryStates}
            errorMessage={errors.state}
            placeholder={t('lenderInformation.form.lenderState')}
            value={values?.state}
            onChange={(e) => handleInputChange('state', e.target.value)}
          />
          <TextInput
            label={t('lenderInformation.form.lenderZipCode')}
            className={'iLendingCDX-mb-6'}
            name="zipCode"
            value={values?.zipCode || ''}
            errorMessage={errors.zipCode}
            onChange={(e) => handleInputChange('zipCode', e.target.value)}
          />
          <PhoneTextInput
            label={t('lenderInformation.form.lenderPhoneNumber')}
            className={'iLendingCDX-mb-6'}
            name="phoneNumber"
            errorMessage={errors.phoneNumber}
            value={values.phoneNumber}
            onChange={(value) => handleInputChange('phoneNumber', value as ILenderDetailsForm['phoneNumber'])}
          />
          <TextInput
            label={t('lenderInformation.form.loanAccountNumber')}
            className={'iLendingCDX-mb-6'}
            name="accountNumber"
            errorMessage={errors.accountNumber}
            onChange={(e) => handleInputChange('accountNumber', e.target.value)}
          />
          <PriceInput
            label={t('lenderInformation.form.remainingLoanBalance')}
            className={'iLendingCDX-mb-6'}
            errorMessage={errors.currentBalance}
            value={values.currentBalance}
            onChange={(value) => handleInputChange('currentBalance', Number(value) as number)}
          />

          <Button
            type="submit"
            className="iLendingCDX-button--rounded"
            size="lg"
            isLoading={isLoading}
          >
            {t('lenderInformation.form.continue')}
          </Button>
        </div>
      </form>
    </div>
  );
};
export default LenderInformationForm;
